/**
 * Personal task list against the FEA-08 tasks contract: list/filter the
 * caller's own tasks, create, quick status updates, delete, and — in the
 * expandable details panel — edit the embedded blockers, dependencies, and
 * attachments arrays. Per the contract, those arrays are replaced wholesale
 * on PUT, so every edit resends the full updated array; the server owns
 * entry ids and reporter/timestamp fields.
 *
 * NOTE (FEA-25): the list here still uses its original hand-rolled fetch
 * rather than `usePagedList` — its error handling already resolves the
 * spinner (`setTasks([])` on failure), so the primitive's main benefit
 * (fixing a stuck spinner) doesn't apply, and this is the biggest page in
 * the app with four separate nested mutation flows (status, blockers,
 * dependencies, attachments) built on `task` object identity passed down to
 * `TaskRow`/`TaskDetails`. Migrating the list AND hardening every mutation
 * in the same pass risked a regression with no corresponding bug to fix, so
 * per the finding's own optionality clause this sweep applied confirms,
 * toasts, and the prompt→Modal swap only, leaving the fetch as-is.
 */
import { useCallback, useEffect, useState } from 'react';
import * as tasksApi from '../api/tasks';
import * as filesApi from '../api/files';
import { useConfirm } from '../lib/confirm';
import { useToast } from '../components/toast';
import Modal from '../components/Modal';
import {
  ErrorAlert,
  Spinner,
  EmptyState,
  Field,
  TextareaField,
  SelectField,
  StatusPill,
  formatDate
} from '../components/ui';
import { ui, cn } from '../lib/ui';

const STATUSES = [
  'not_started',
  'in_progress',
  'scheduled',
  'pending',
  'in_review',
  'on_hold',
  'blocked',
  'completed'
];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];
const BLOCKER_TYPES = ['technical', 'resource', 'approval', 'external', 'other'];
const DEPENDENCY_TYPES = [
  'finish_to_start',
  'start_to_start',
  'finish_to_finish',
  'start_to_finish'
];

export default function Tasks() {
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState('');
  const [tasks, setTasks] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null); // task id with open details

  // Create form
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('medium');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [createError, setCreateError] = useState(null);

  const reload = useCallback(() => {
    setTasks(null);
    setError(null);
    tasksApi
      .listTasks(statusFilter ? { status: statusFilter } : undefined)
      .then((d) => setTasks(d.tasks || []))
      .catch((err) => {
        setTasks([]);
        setError(err);
      });
  }, [statusFilter]);

  useEffect(reload, [reload]);

  async function onCreate(event) {
    event.preventDefault();
    setCreateError(null);
    setBusy(true);
    try {
      await tasksApi.createTask({
        title,
        priority,
        endDate: endDate ? new Date(endDate).toISOString() : undefined,
        notes: notes || undefined
      });
      setTitle('');
      setPriority('medium');
      setEndDate('');
      setNotes('');
      toast.success(`Created "${title}".`);
      reload();
    } catch (err) {
      setCreateError(err);
      toast.error(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1 className={ui.h1}>Tasks</h1>

      <div className={cn(ui.grid, ui.gridMd2)}>
        <section className={ui.card}>
          <h3 className={ui.h3}>New task</h3>
          <ErrorAlert error={createError} />
          <form onSubmit={onCreate}>
            <Field
              label="Title"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <SelectField label="Priority" value={priority} onChange={(e) => setPriority(e.target.value)}>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </SelectField>
            <Field
              label="Due date (optional)"
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            <TextareaField
              label="Notes (optional)"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <button className={ui.btnPrimary} type="submit" disabled={busy}>
              {busy ? 'Creating…' : 'Create task'}
            </button>
          </form>
        </section>

        <section className={ui.card}>
          <h3 className={ui.h3}>Your tasks</h3>
          <SelectField
            label="Filter by status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">all</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replace(/_/g, ' ')}
              </option>
            ))}
          </SelectField>
          <ErrorAlert error={error} />
          {tasks === null ? (
            <Spinner />
          ) : tasks.length === 0 ? (
            !error && <EmptyState>No tasks{statusFilter ? ' with this status' : ''}.</EmptyState>
          ) : (
            <div className={ui.tableWrap}>
              <table className={ui.table}>
                <thead>
                  <tr>
                    <th>Task</th>
                    <th>Status</th>
                    <th>Priority</th>
                    <th>Due</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((task) => (
                    <TaskRow
                      key={task.id}
                      task={task}
                      allTasks={tasks}
                      open={expanded === task.id}
                      onToggle={() =>
                        setExpanded((current) => (current === task.id ? null : task.id))
                      }
                      onChanged={reload}
                      toast={toast}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </>
  );
}

function TaskRow({ task, allTasks, open, onToggle, onChanged, toast }) {
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function setStatus(status) {
    setError(null);
    setBusy(true);
    try {
      await tasksApi.updateTask(task.id, {
        status,
        // Contract nicety: a completed task reads as 100%.
        ...(status === 'completed' ? { percentageComplete: 100 } : {})
      });
      toast.success(`Status updated to ${status.replace(/_/g, ' ')}.`);
      onChanged();
    } catch (err) {
      setError(err);
      toast.error(err);
      setBusy(false);
    }
  }

  async function remove() {
    const ok = await confirm({
      title: 'Delete task?',
      message: `Delete "${task.title}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true
    });
    if (!ok) return;
    setError(null);
    setBusy(true);
    try {
      await tasksApi.deleteTask(task.id);
      toast.success(`Deleted "${task.title}".`);
      onChanged();
    } catch (err) {
      setError(err);
      toast.error(err);
      setBusy(false);
    }
  }

  return (
    <>
      <tr>
        <td>
          {task.title}
          {task.notes && <div className={cn(ui.muted, 'text-body-s')}>{task.notes}</div>}
          <ErrorAlert error={error} />
        </td>
        <td>
          <StatusPill status={task.status} />
          <div>
            <select
              className={ui.input}
              aria-label={`Change status of ${task.title}`}
              value={task.status}
              disabled={busy}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>
        </td>
        <td>
          <StatusPill status={task.priority} />
        </td>
        <td>{formatDate(task.endDate)}</td>
        <td>
          <div className={ui.row}>
            <button className={ui.btnSecondary} onClick={onToggle}>
              {open ? 'Hide' : 'Details'}
            </button>
            <button className={ui.btnDanger} onClick={remove} disabled={busy}>
              Delete
            </button>
          </div>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5}>
            <TaskDetails task={task} allTasks={allTasks} onChanged={onChanged} toast={toast} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Blockers / dependencies / attachments editor. Every mutation PUTs the full
 * updated array (wholesale-replacement contract) and reloads the list.
 */
function TaskDetails({ task, allTasks, onChanged, toast }) {
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Add-blocker form
  const [blockerDescription, setBlockerDescription] = useState('');
  const [blockerType, setBlockerType] = useState('technical');

  // Add-dependency form
  const otherTasks = allTasks.filter((t) => t.id !== task.id);
  const [dependencyTaskId, setDependencyTaskId] = useState('');
  const [dependencyType, setDependencyType] = useState('finish_to_start');

  // Resolve-blocker modal (replaces the old native-prompt flow)
  const [resolvingBlocker, setResolvingBlocker] = useState(null); // blocker object or null
  const [resolutionText, setResolutionText] = useState('');

  async function putArray(patch, successMessage) {
    setError(null);
    setBusy(true);
    try {
      await tasksApi.updateTask(task.id, patch);
      if (successMessage) toast.success(successMessage);
      onChanged();
    } catch (err) {
      setError(err);
      toast.error(err);
      setBusy(false);
    }
  }

  function addBlocker(event) {
    event.preventDefault();
    putArray(
      {
        blockers: [...(task.blockers || []), { description: blockerDescription, type: blockerType }]
      },
      'Blocker added.'
    );
    setBlockerDescription('');
    setBlockerType('technical');
  }

  function openResolveBlocker(blocker) {
    setResolutionText('');
    setResolvingBlocker(blocker);
  }

  function confirmResolveBlocker(event) {
    event.preventDefault();
    const resolution = resolutionText.trim() || 'resolved';
    const blockerId = resolvingBlocker.id;
    setResolvingBlocker(null);
    putArray(
      {
        blockers: (task.blockers || []).map((b) =>
          b.id === blockerId ? { ...b, resolvedAt: new Date().toISOString(), resolution } : b
        )
      },
      'Blocker resolved.'
    );
  }

  async function removeBlocker(blocker) {
    const ok = await confirm({
      title: 'Remove blocker?',
      message: `Remove "${blocker.description}"? This cannot be undone.`,
      confirmLabel: 'Remove',
      danger: true
    });
    if (!ok) return;
    putArray(
      { blockers: (task.blockers || []).filter((b) => b.id !== blocker.id) },
      'Blocker removed.'
    );
  }

  function addDependency(event) {
    event.preventDefault();
    putArray(
      {
        dependencies: [
          ...(task.dependencies || []),
          { dependentTaskId: dependencyTaskId, type: dependencyType }
        ]
      },
      'Dependency added.'
    );
    setDependencyTaskId('');
    setDependencyType('finish_to_start');
  }

  async function removeDependency(dependency) {
    const ok = await confirm({
      title: 'Remove dependency?',
      message: 'Remove this dependency? This cannot be undone.',
      confirmLabel: 'Remove',
      danger: true
    });
    if (!ok) return;
    putArray(
      { dependencies: (task.dependencies || []).filter((d) => d.id !== dependency.id) },
      'Dependency removed.'
    );
  }

  async function addAttachment(file) {
    setError(null);
    setBusy(true);
    try {
      const uploaded = await filesApi.uploadFile(file);
      const record = uploaded.file || uploaded;
      const fileId = record.fileId || record.id;
      await tasksApi.updateTask(task.id, {
        attachments: [
          ...(task.attachments || []),
          {
            id: fileId,
            fileName: record.fileName || file.name,
            fileUrl: `/files/${fileId}/download-url`,
            fileSize: record.fileSize || file.size,
            fileType: record.mimeType || file.type || 'application/octet-stream'
          }
        ]
      });
      toast.success(`Attached "${file.name}".`);
      onChanged();
    } catch (err) {
      setError(err);
      toast.error(err);
      setBusy(false);
    }
  }

  async function removeAttachment(attachment) {
    const ok = await confirm({
      title: 'Remove attachment?',
      message: `Remove "${attachment.fileName}"? This cannot be undone.`,
      confirmLabel: 'Remove',
      danger: true
    });
    if (!ok) return;
    putArray(
      { attachments: (task.attachments || []).filter((a) => a.id !== attachment.id) },
      'Attachment removed.'
    );
  }

  async function openAttachment(attachmentId) {
    setError(null);
    try {
      // May be rejected while malware scanning has not cleared the upload
      // (fail-closed backend policy) — surface the API's error as-is.
      const { downloadUrl } = await filesApi.getDownloadUrl(attachmentId);
      window.open(downloadUrl, '_blank', 'noopener');
    } catch (err) {
      setError(err);
      toast.error(err);
    }
  }

  const taskTitle = (taskId) => {
    const match = allTasks.find((t) => t.id === taskId);
    return match ? match.title : taskId;
  };

  return (
    <div>
      <ErrorAlert error={error} />

      <h4>Blockers</h4>
      {(task.blockers || []).length === 0 ? (
        <EmptyState>No blockers reported.</EmptyState>
      ) : (
        <ul>
          {(task.blockers || []).map((blocker) => (
            <li key={blocker.id} className="mt-4">
              <StatusPill status={blocker.resolvedAt ? 'completed' : 'blocked'} />{' '}
              {blocker.description} <span className={cn(ui.muted, 'text-body-s')}>({blocker.type})</span>
              {blocker.resolvedAt ? (
                <span className={cn(ui.muted, 'text-body-s')}> — resolved: {blocker.resolution}</span>
              ) : (
                <span className={ui.row}>
                  <button
                    className={ui.btnSecondary}
                    disabled={busy}
                    onClick={() => openResolveBlocker(blocker)}
                  >
                    Resolve
                  </button>
                  <button
                    className={ui.btnDanger}
                    disabled={busy}
                    onClick={() => removeBlocker(blocker)}
                  >
                    Remove
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={addBlocker} className={cn(ui.row, 'mt-4')}>
        <Field
          label="New blocker"
          required
          placeholder="What is blocking this task?"
          value={blockerDescription}
          onChange={(e) => setBlockerDescription(e.target.value)}
        />
        <SelectField label="Type" value={blockerType} onChange={(e) => setBlockerType(e.target.value)}>
          {BLOCKER_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </SelectField>
        <button className={ui.btnPrimary} type="submit" disabled={busy}>
          Add blocker
        </button>
      </form>

      <h4 className="mt-4">Dependencies</h4>
      {(task.dependencies || []).length === 0 ? (
        <EmptyState>No dependencies.</EmptyState>
      ) : (
        <ul>
          {(task.dependencies || []).map((dependency) => (
            <li key={dependency.id} className="mt-4">
              {dependency.type.replace(/_/g, ' ')} → {taskTitle(dependency.dependentTaskId)}
              {dependency.description && (
                <span className={cn(ui.muted, 'text-body-s')}> — {dependency.description}</span>
              )}{' '}
              <button
                className={ui.btnDanger}
                disabled={busy}
                onClick={() => removeDependency(dependency)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      {otherTasks.length > 0 && (
        <form onSubmit={addDependency} className={cn(ui.row, 'mt-4')}>
          <SelectField
            label="Depends on task"
            required
            value={dependencyTaskId}
            onChange={(e) => setDependencyTaskId(e.target.value)}
          >
            <option value="">choose a task…</option>
            {otherTasks.map((t) => (
              <option key={t.id} value={t.id}>
                {t.title}
              </option>
            ))}
          </SelectField>
          <SelectField
            label="Type"
            value={dependencyType}
            onChange={(e) => setDependencyType(e.target.value)}
          >
            {DEPENDENCY_TYPES.map((t) => (
              <option key={t} value={t}>
                {t.replace(/_/g, ' ')}
              </option>
            ))}
          </SelectField>
          <button className={ui.btnPrimary} type="submit" disabled={busy}>
            Add dependency
          </button>
        </form>
      )}

      <h4 className="mt-4">Attachments</h4>
      {(task.attachments || []).length === 0 ? (
        <EmptyState>No attachments.</EmptyState>
      ) : (
        <ul>
          {(task.attachments || []).map((attachment) => (
            <li key={attachment.id} className="mt-4">
              <button
                type="button"
                className={ui.btnSecondary}
                onClick={() => openAttachment(attachment.id)}
              >
                Open {attachment.fileName}
              </button>{' '}
              <button
                className={ui.btnDanger}
                disabled={busy}
                onClick={() => removeAttachment(attachment)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      <label className={cn(ui.field, 'mt-4')}>
        <span className={ui.label}>Attach a file</span>
        <input
          className={ui.input}
          type="file"
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files[0];
            e.target.value = '';
            if (file) addAttachment(file);
          }}
        />
      </label>

      <Modal
        open={Boolean(resolvingBlocker)}
        onClose={() => setResolvingBlocker(null)}
        title="Resolve blocker"
      >
        {resolvingBlocker && (
          <form onSubmit={confirmResolveBlocker}>
            <p className="text-body-s">
              <strong>{resolvingBlocker.description}</strong>
            </p>
            <TextareaField
              label="How was this blocker resolved?"
              rows={3}
              value={resolutionText}
              onChange={(e) => setResolutionText(e.target.value)}
            />
            <div className={cn(ui.row, 'mt-4')}>
              <button
                type="button"
                className={ui.btnSecondary}
                onClick={() => setResolvingBlocker(null)}
              >
                Cancel
              </button>
              <button type="submit" className={ui.btnPrimary} disabled={busy}>
                Resolve
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
